import type { ConversationListResponse } from '@/features/conversation';
import type { Conversation } from '@/features/conversation/models/conversation';
import { conversationKeys } from '@/features/conversation/queries/conversation-keys';
import type { InfiniteData, QueryClient } from '@tanstack/react-query';

/**
 * Utility function to update a conversation in the infinite query cache
 * @param queryClient - React Query client instance
 * @param conversationId - ID of the conversation to update
 * @param updatedData - Partial conversation data to update (can contain string timestamps)
 */
export const updateConversationInCache = (
  queryClient: QueryClient,
  conversationId: string,
  updatedData: Record<string, any>,
  action: 'update' | 'add' = 'update'
) => {
  queryClient.setQueriesData<InfiniteData<ConversationListResponse>>(
    { queryKey: conversationKeys.all },
    (oldData: any) => {
      if (!oldData?.pages) return oldData;

      return {
        ...oldData,
        pages: oldData.pages.map((page: ConversationListResponse) => {
          if (action === 'update') {
            if (!page) return page;
            const hasConversation = page.conversations.some(
              (conv: Conversation) => conv.conversationId === conversationId
            );
            if (!hasConversation) return page;
            const updatedConversations = page.conversations.map((conv: Conversation) => {
              if (conv.conversationId === conversationId) {
                return {
                  ...conv,
                  ...updatedData,
                  conversationId: conversationId
                };
              }
              return conv;
            });

            return {
              ...page,
              conversations: updatedConversations
            };
          }

          return {
            ...page,
            conversations: [updatedData as Conversation, ...(page.conversations || [])]
          };
        })
      };
    }
  );
};
